import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { HONEYMOON_TAG } from '@shared/constants/accommodations';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import type { Maybe } from '@graphql/generated-base';

type PrioritizedTagPillsProps = Readonly<{
  /** `listing.tags` — the curated tags travel_service matched on this property. */
  tags?: Maybe<ReadonlyArray<string>>;
}>;

/**
 * Names why a stay is near the top: the curated tag it matched.
 *
 * Only ever appears when the guest asked for the lift. travel_service populates `tags` on a
 * property *only* when the request carried `prioritizeTags`, so an unchecked filter means no tags
 * come back and no pill renders — the badge and the ordering cannot disagree.
 *
 * Sits in the same slot as CouplesPick, above the name, rather than over the photo: the photo's
 * bottom-left corner already belongs to SponsoredTag, and a sponsored honeymoon stay would collide
 * there.
 */
export const PrioritizedTagPills = ({ tags }: PrioritizedTagPillsProps) => {
  const { tq } = useTranslation('accommodationsConsole');
  const { isMobileOrTablet } = useAccommodationsListingMobileLayout();

  /**
   * Unlabelled tags render nothing rather than falling back to the raw slug — a badge reading
   * `spa_luxury` looks like a bug on a guest-facing card. The filter chooser takes the opposite
   * view and does show the raw tag, because there an unlabelled row is still a working control
   * (see PrioritizeTagsFacet). Both maps grow together when the vocabulary does; the vocabulary
   * itself is `SUPPORTED_TAGS` in travel_inventory_service.
   */
  const labelForTag = (tag: string): string | null => {
    switch (tag) {
      case HONEYMOON_TAG:
        return tq`Honeymoon`;
      default:
        return null;
    }
  };

  const labelled = (tags ?? []).map(tag => ({ tag, label: labelForTag(tag) })).filter((entry): entry is { tag: string; label: string } => !!entry.label);

  if (!labelled.length) {
    return null;
  }

  return (
    <Flex
      width="100%"
      gap={2}
      flexWrap="wrap"
      justifyContent={{ _: 'flex-start', md: 'flex-end' }}
    >
      {labelled.map(({ tag, label }) => (
        <Box
          key={tag}
          paddingX={3}
          paddingY={1}
          borderRadius={2}
          color="primary8"
          backgroundColor="primary2"
        >
          <TextV2 typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}>{label}</TextV2>
        </Box>
      ))}
    </Flex>
  );
};
